import { useState } from 'react';
import { AccordionContent, AccordionItem, AccordionTrigger, Button } from 'components';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/tooltip';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/forms';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import { CheckIcon, PlusIcon } from '@radix-ui/react-icons';
import { TriangleAlert } from 'lucide-react';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import type { AutoSaveStatus } from 'hooks/useDebouncedFieldAutosave';
import { useVitalParametersEditorContext } from 'providers/VitalParametersEditorProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/dropdown';
import {
  getImpossibleMessageParams,
  getPathologicalTooltipParams,
  getVitalParameterStatus,
} from 'utils/vitalParameterBoundaries';

const VITAL_INT_RE = /^\d+$/;
const VITAL_FLOAT_RE = /^\d+(?:,\d+)?$/;

export const VITAL_PARAMETER_KEYS = [
  'systolicMmhg',
  'diastolicMmhg',
  'pulseBpm',
  'spo2Percent',
  'temperatureC',
  'respiratoryRateBpm',
  'weightKg',
  'heightCm',
] as const;

export type VitalParameterKey = (typeof VITAL_PARAMETER_KEYS)[number];

// Backend types these as IntegerField; the rest are FloatField.
const INTEGER_VITAL_KEYS: ReadonlySet<VitalParameterKey> = new Set([
  'systolicMmhg',
  'diastolicMmhg',
  'pulseBpm',
  'spo2Percent',
  'respiratoryRateBpm',
]);

interface VitalParametersSectionProps {
  autoStatus: Record<string, AutoSaveStatus>;
}

export const VitalParametersSection = ({ autoStatus }: VitalParametersSectionProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const {
    currentVitalParameters: vitalParameters,
    handleVitalParameterChange: onChange,
    vitalParameterSelectionMap: selectionMap,
    handleVitalParameterSelectionToggle: onSelectionToggle,
  } = useVitalParametersEditorContext();
  const [addedKeys, setAddedKeys] = useState<Set<VitalParameterKey>>(new Set());

  const hasValue = (key: VitalParameterKey) => {
    const val = vitalParameters[key] as unknown;
    return val !== null && val !== undefined && val !== '';
  };

  const isVisible = (key: VitalParameterKey) => hasValue(key) || addedKeys.has(key);

  const availableKeys = VITAL_PARAMETER_KEYS.filter((key) => !isVisible(key));

  const handleAddKey = (key: VitalParameterKey) => {
    setAddedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const getLabelsAndUnits = (key: VitalParameterKey): { label: string; unit?: string } => {
    switch (key) {
      case 'systolicMmhg':
        return { label: t('vitalParameters.bloodPressureSys'), unit: 'mmHg' };
      case 'diastolicMmhg':
        return { label: t('vitalParameters.bloodPressureDia'), unit: 'mmHg' };
      case 'pulseBpm':
        return { label: t('vitalParameters.heartRate'), unit: 'bpm' };
      case 'spo2Percent':
        return { label: t('vitalParameters.oxygenSaturation'), unit: '%' };
      case 'temperatureC':
        return { label: t('vitalParameters.temperature'), unit: '°C' };
      case 'respiratoryRateBpm':
        return { label: t('vitalParameters.respiratoryRate'), unit: '/min' };
      case 'weightKg':
        return { label: t('vitalParameters.weight'), unit: 'kg' };
      case 'heightCm':
        return { label: t('vitalParameters.height'), unit: 'cm' };
      default:
        return { label: key };
    }
  };

  const renderField = (key: VitalParameterKey) => {
    const { label, unit } = getLabelsAndUnits(key);
    const isIntegerField = INTEGER_VITAL_KEYS.has(key);
    const rawValue = vitalParameters[key];
    // Display is purely visual: the state holds dot-format so the backend can
    // parse it directly; we localize the rendered string for the user.
    const value =
      rawValue === null || rawValue === undefined ? '' : String(rawValue).replaceAll('.', ',');
    const isChecked = selectionMap[key] ?? false;
    const status = autoStatus[key];

    const regex = isIntegerField ? VITAL_INT_RE : VITAL_FLOAT_RE;
    const showFormatError = value !== '' && !regex.test(value);
    const numericValue = showFormatError || value === '' ? null : Number(value.replace(',', '.'));
    const paramStatus = getVitalParameterStatus(key, numericValue);
    const pathologicalParams =
      paramStatus === 'pathological' ? getPathologicalTooltipParams(key) : null;
    const impossibleParams = paramStatus === 'impossible' ? getImpossibleMessageParams(key) : null;
    const tooltipMessage = pathologicalParams
      ? t('vitalParameters.pathologicalTooltip', pathologicalParams)
      : impossibleParams
        ? t('vitalParameters.impossibleMessage', impossibleParams)
        : null;
    const hasErrorBorder = showFormatError || paramStatus === 'impossible';

    return (
      <div key={key} className="flex items-center gap-2 py-1" data-ph-no-autocapture>
        <Checkbox
          checked={isChecked}
          onCheckedChange={(checkedValue) => {
            const isCheckedNext = checkedValue === true;
            trackEvent(ANALYTICS_EVENTS.RESULT_VITAL_PARAMETER_TOGGLED, {
              checked: isCheckedNext,
              title: label,
            });
            onSelectionToggle(key, isCheckedNext);
          }}
          disabled={!value}
          aria-label={`Select ${label}`}
          data-ph-no-autocapture
        />
        <div className="flex flex-1 items-center gap-2">
          <label className="text-sm font-medium w-32 shrink-0 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {label}
          </label>
          <div className="w-5 shrink-0 flex items-center justify-center">
            {tooltipMessage && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={`cursor-default ${paramStatus === 'impossible' ? 'text-destructive' : 'text-warning'}`}
                    aria-label={t('vitalParameters.pathologicalWarning')}
                  >
                    <TriangleAlert className="h-4 w-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{tooltipMessage}</TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex flex-col gap-1 w-32">
            <div className="relative w-full">
              <Input
                value={value}
                onChange={(e) => onChange(key, e.target.value.replaceAll(',', '.'))}
                inputMode={isIntegerField ? 'numeric' : 'decimal'}
                aria-label={label}
                aria-invalid={showFormatError || undefined}
                className={`w-full text-right ${unit ? 'pr-20' : 'pr-8'} ${hasErrorBorder ? 'border-destructive' : ''}`}
                placeholder={t('vitalParameters.enterValue')}
              />
              {status === 'saved' && !showFormatError && paramStatus === 'normal' && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-success">
                  <CheckIcon className="h-4 w-4" />
                </span>
              )}
              {unit && (
                <span className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                  {unit}
                </span>
              )}
            </div>
            {showFormatError && (
              <p className="text-xs text-destructive" role="alert">
                {isIntegerField
                  ? t('vitalParameters.invalidIntegerFormat')
                  : t('vitalParameters.invalidNumberFormat')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AccordionItem value="vitalParameters">
      <AccordionTrigger variant="arrow" className="text-muted-foreground" data-ph-no-autocapture>
        {t('result.section.vitalParameters', 'Vitalparameter')}
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-1 p-1">
          {VITAL_PARAMETER_KEYS.filter(isVisible).map((key) => renderField(key))}

          {availableKeys.length > 0 && (
            <div className="pt-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 gap-1">
                    <PlusIcon className="h-3.5 w-3.5" />
                    <span className="text-xs">{t('general.add')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {availableKeys.map((key) => {
                    const { label } = getLabelsAndUnits(key);
                    return (
                      <DropdownMenuItem key={key} onSelect={() => handleAddKey(key)}>
                        {label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
